import { CheckIcon } from '@phosphor-icons/react'
import moment from 'moment'
import type { CSSProperties, ReactElement } from 'react'
import { Button } from 'app/components/ui/button'
import { hueClasses } from 'app/lib/chipColors'
import { cn } from 'app/lib/utils'
import { notify } from 'app/services/notify.client'
import type { Visit, VisitTone } from 'app/types/appointment-calendar'
import { CALLED_OFF_TONES } from 'lib/appointmentCalendar'
import { formatRange } from 'lib/dayClock'
import type { AppointmentsCalendarState } from './useAppointmentsCalendar'

const TONE_LABEL: Readonly<Record<VisitTone, string>> = {
  scheduled: `Scheduled`,
  confirmed: `Confirmed`,
  completed: `Completed`,
  cancelled: `Cancelled`,
  broken: `Broken`,
}

/** The block's fill and hairline. Literal so Tailwind keeps them. */
const TONE_BLOCK: Readonly<Record<VisitTone, string>> = {
  scheduled: hueClasses.blue,
  confirmed: hueClasses.green,
  // A surface rather than the gray chip tint, which turns loud in dark mode: done work should recede.
  completed: `border-default bg-surface-muted`,
  cancelled: `border-dashed border-(--chip-gray-border) bg-transparent opacity-70`,
  broken: cn(hueClasses.red, `border-dashed opacity-80`),
}

/** The same tone as a dot, for rows and the legend. */
const TONE_DOT: Readonly<Record<VisitTone, string>> = {
  scheduled: `bg-(--chip-blue-ink)`,
  confirmed: `bg-(--chip-green-ink)`,
  completed: `bg-(--chip-gray-ink) opacity-50`,
  cancelled: `border border-dashed border-(--chip-gray-ink) bg-transparent`,
  broken: `bg-(--chip-red-ink)`,
}

type VisitProps = {
  visit: Visit
  calendar: Pick<AppointmentsCalendarState, `patientFor` | `describe` | `openContact`>
}

function visitText({ visit, calendar }: VisitProps) {
  const patient = calendar.patientFor(visit.appointment.patientId)
  const name = patient?.name || (patient ? `Unknown patient` : ``)
  const what = visit.appointment.type || visit.appointment.description || ``
  const time = formatRange(visit.start, visit.end)
  const providers = visit.providerIds
    .map((id) => calendar.describe(visit.appointment.locationId, id)?.name ?? id)
    .join(` and `)
  const open = () => {
    if (patient?.contactId) calendar.openContact(patient.contactId)
    else notify(`This patient has no contact in Trackable yet.`)
  }
  const title = [name, time, what, TONE_LABEL[visit.tone], providers && `with ${providers}`].filter(Boolean).join(`, `)
  return { name, what, time, providers, open, title }
}

function PatientName({ name, visit }: { name: string; visit: Visit }): ReactElement {
  return (
    <span className="flex w-full min-w-0 items-center gap-1">
      <span className={cn(`truncate font-semibold text-strong`, CALLED_OFF_TONES.has(visit.tone) && `line-through`)}>
        {/* A placeholder bar while the name loads, so the block keeps its shape. */}
        {name || <span className="inline-block h-2.5 w-20 animate-pulse rounded bg-surface-muted align-middle" />}
      </span>
      {visit.tone === `confirmed` && <CheckIcon size={12} weight="bold" className="shrink-0 text-(--chip-green-ink)" />}
    </span>
  )
}

/**
 * A visit drawn to scale on the day board. Lines that do not fit its height are clipped, name first.
 * The board places it through `--visit-top`, `--visit-left`, `--visit-height` and `--visit-width`.
 */
export function VisitBlock({ style, ...props }: VisitProps & { style: CSSProperties }): ReactElement {
  const { name, what, time, open, title } = visitText(props)
  return (
    <Button
      type="button"
      variant="ghost"
      size="inline"
      title={title}
      onClick={open}
      style={style}
      className="absolute left-(--visit-left) top-(--visit-top) h-(--visit-height) w-(--visit-width) items-stretch justify-stretch overflow-hidden hover:z-10"
    >
      <span
        className={cn(
          `flex min-w-0 flex-1 flex-col overflow-hidden whitespace-normal rounded-md border px-1.5 py-1 text-left text-body-xs leading-4 group-hover/button:shadow-elevation-2`,
          TONE_BLOCK[props.visit.tone],
        )}
      >
        <PatientName name={name} visit={props.visit} />
        <span className="w-full truncate tabular-nums text-subtle">{time}</span>
        {what && <span className="w-full truncate text-subtle">{what}</span>}
      </span>
    </Button>
  )
}

/** A visit as one line of a list: the week's day columns and the phone agenda. */
export function VisitRow({ withProvider, ...props }: VisitProps & { withProvider?: boolean }): ReactElement {
  const { name, what, providers, open, title } = visitText(props)
  const { visit } = props
  return (
    <Button type="button" variant="ghost" size="row" title={title} onClick={open} className="items-start">
      <span className={cn(`mt-1.5 size-2 shrink-0 rounded-full`, TONE_DOT[visit.tone])} />
      {/* No meridiem: a practice's day reads unambiguously, and the full range is in the title. */}
      <span className="w-9 shrink-0 pt-px text-right text-body-xs tabular-nums text-subtle">
        {moment().startOf(`day`).add(visit.start, `minutes`).format(`h:mm`)}
      </span>
      <span className="flex min-w-0 flex-1 flex-col text-body-sm">
        <PatientName name={name} visit={visit} />
        {(what || (withProvider && providers)) && (
          <span className="truncate text-body-xs text-subtle">
            {[what, withProvider && providers].filter(Boolean).join(` · `)}
          </span>
        )}
      </span>
    </Button>
  )
}

const LEGEND: readonly VisitTone[] = [`scheduled`, `confirmed`, `completed`]

export function VisitLegend(): ReactElement {
  return (
    <span className="flex items-center gap-3 text-body-xs text-subtle max-md:hidden">
      {LEGEND.map((tone) => (
        <span key={tone} className="flex items-center gap-1.5">
          <span className={cn(`size-2 rounded-full`, TONE_DOT[tone])} />
          {TONE_LABEL[tone]}
        </span>
      ))}
    </span>
  )
}
